
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { HlmSidebarImports } from '@spartan-ng/helm/sidebar';
import { HlmCollapsibleImports } from '@spartan-ng/helm/collapsible';
import { lucideCalendar, lucideHouse, lucideClipboardClock, lucideChevronsUpDown, lucideBarChart3, lucideUsers, lucideMonitor, lucidePieChart, lucidePuzzle, lucideSettings, lucideBuilding2, lucideDownload, lucideBook, lucideMail, lucideChevronRight, lucideUser, lucideMegaphone, lucideLogOut, lucideEllipsisVertical, lucideExternalLink } from '@ng-icons/lucide';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { ThemeEnum } from 'src/app/services/common/theming.service';
import { ConstantService, UserRoles } from 'src/app/services/common/constant.service';
import { HlmAvatarImports } from '@spartan-ng/helm/avatar';
import { HasPermissionService } from 'src/app/services/common/has-permission.service';
import { HasPermissionDirective } from 'src/app/services/common/has-permission.directive';
import { AuthService } from 'src/app/authentication/auth.service';
import { User } from 'oidc-client';
import { HlmAccordionImports } from '@spartan-ng/helm/accordion';
import { SidebarStore } from './sidebar-store';
import { TenantSwitcher } from '../tenant-switcher/tenant-switcher';

@Component({
  selector: 'app-sidebar',
  imports: [
    HlmSidebarImports, HlmCollapsibleImports, HlmDropdownMenuImports, HlmAvatarImports, HlmAccordionImports,
    NgIcon, TranslatePipe, RouterLink, HasPermissionDirective, TenantSwitcher
  ],
  templateUrl: './sidebar.html',
  providers: [
    provideIcons({
      lucideHouse,
      lucideCalendar,
      lucideClipboardClock,
      lucideChevronsUpDown,
      lucideBarChart3,
      lucideUsers,
      lucideMonitor,
      lucidePieChart,
      lucidePuzzle,
      lucideSettings,
      lucideBuilding2,
      lucideDownload,
      lucideBook,
      lucideMail,
      lucideChevronRight,
      lucideUser,
      lucideMegaphone,
      lucideLogOut,
      lucideEllipsisVertical,
      lucideExternalLink,
    }),
  ],
})
export class Sidebar implements OnInit {

  protected readonly store = inject(SidebarStore);
  private readonly authService = inject(AuthService);
  private readonly hasPermissionService = inject(HasPermissionService);
  private readonly constantService = inject(ConstantService);
  
  protected logoUrl = this.constantService.getImageFromServer(ThemeEnum.companyLogo) ?? ThemeEnum.companyLogoDefault;
  protected hasTenantSwitcher = computed(() => this.hasPermissionService.allowAccess([UserRoles.SuperAdmin, UserRoles.MspAdmin]));
  protected hasAdminMenu = computed(() => this.hasPermissionService.allowAccess([UserRoles.AdminUser, UserRoles.HelpDeskUser, UserRoles.Marketing, UserRoles.MspAdmin, UserRoles.SuperAdmin]));
  protected user: User | null = null;
  protected tenantSwitcherState = signal<'closed' | 'open'>('closed');
  
  protected readonly userMenu = [
    {
      title: 'Menu.MyApplications',
      url: 'my-apps',
      icon: 'lucideHouse',
    },
    {
      title: 'Menu.RecentActivity',
      url: '/recent-activities',
      icon: 'lucideClipboardClock',
    },
    {
      title: 'Menu.SystemStatus',
      url: '/system-status',
      icon: 'lucideCalendar',
    }
  ];

  protected readonly adminMenu = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      icon: 'lucideBarChart3',
    },
    {
      title: 'Management',
      icon: 'lucideUsers',
      items: [
        { title: 'Users', url: '/users' },
        { title: 'Group', url: '/groups/group-list' },
        { title: 'Applications', url: '/application' },
        { title: 'Conditional Access', url: '/conditional-access/settings' }
      ],
    },
    {
      title: 'Smart Monitoring',
      icon: 'lucideMonitor',
      items: [
        { title: 'Threat Monitoring', url: '/monitoring/threat-monitor' },
        { title: 'Logging', url: '/monitoring/activity-logging' },
      ],
    },
    {
      title: 'Reports',
      icon: 'lucidePieChart',
      items: [
        { title: 'Embedded Reports', url: '/reports/embedded-reports' },
        { title: 'Automated Reports', url: '/reports/automated-reports' },
        { title: 'MSP Report', url: '/reports/msp', permission: ['mspadmin'] },
      ],
    },
    {
      title: 'Integration',
      icon: 'lucidePuzzle',
      items: [
        { title: 'Cloud', url: '/cloud' },
        { title: 'Premises', url: '/premises' },
        { title: 'Login Agents', url: '/integration/agents' },
        { title: 'Endpoint Agents', url: '/integration/endpointagents' },
        { title: 'Configuration', url: '/integration/configuration' },
      ],
    },
    {
      title: 'Setup',
      icon: 'lucideSettings',
      items: [
        { title: 'Local Theme', url: '/theme/local', permission: ['adminuser', 'superadmin', 'mspadmin'] },
        { title: 'Gateways', url: '/setup/gateway' },
        { title: 'Global Settings', url: '/setup/global-settings' },
        { title: 'Security', url: '/setup/security' },
        { title: 'Credential Reset', url: '/credential-reset/settings' },
        { title: 'AI Models', url: '/setup/ai-models' }
      ],
    },
    {
      title: 'Enterprise',
      icon: 'lucideBuilding2',
      items: [
        { title: 'Organisation', url: '/enterprise/organization', permission: ['adminuser', 'helpdeskuser'] },
        { title: 'Organisations', url: '/enterprise/organization', permission: ['superadmin', 'mspadmin'] },
        { title: 'Licences', url: '/enterprise/license', permission: ['superadmin', 'mspadmin', 'adminuser', 'helpdeskuser'] },
        { title: 'Migrate Organisation', url: '/enterprise/migrate', permission: ['superadmin'] }
      ],
    },
    {
      title: 'Downloads',
      url: '/downloads',
      icon: 'lucideDownload',
    },
    {
      title: 'Documentation',
      url: 'https://documentation.securenvoy.directory/',
      icon: 'lucideBook',
      external: true
    },
    {
      title: 'Send Notification',
      url: '/send-notification',
      icon: 'lucideMail',
    },
  ];

  protected readonly profileMenu = [
    {
      title: 'ProfileMenu.MyProfile',
      url: '/my-profile',
      icon: 'lucideUser',
    },
    {
      title: 'Menu.WhatsNew',
      url: '/whats-new',
      icon: 'lucideMegaphone',
    }
  ];

  async ngOnInit() {
    this.user = await this.authService.getUser();
  }

  protected async signOut() {
    await this.authService.signOut();
  }
}
